// Slack / Notion branch — team-workspace OAuth installs. These are the only sources that
// need a team to exist first (the connect-url routes require `?teamId=` and admin role).
// A first-run solo user has no team, so we ensure one (creating it on the fly via
// useEnsureTeam — bug-447/448) BEFORE minting the connect URL, then run the same OAuth-popup
// + readiness-poll flow as Google/GitHub. The team creator is the owner, so the admin gate
// on the connect-url route passes. Both providers are identical bar copy + endpoint, so one
// component drives both.

import { useEffect, useRef, useState } from 'react';
import { apiFetch } from '../../lib/api';
import { useOAuthPopup } from '../../hooks/useOAuthPopup';
import { useOnboardingReadiness } from '../../hooks/useOnboardingReadiness';
import { useEnsureTeam } from '../../hooks/useTeamSettings';
import { SourceIcon } from '../shared/SourceIcon';

export type TeamProvider = 'slack' | 'notion';

interface TeamSourceCopy {
  crumb: string;
  label: string;
  blurb: string;
  connectUrlPath: string;
}

const TEAM_SOURCE: Record<TeamProvider, TeamSourceCopy> = {
  slack: {
    crumb: 'STEP 02 · SLACK',
    label: 'Slack',
    blurb: 'Installs the Terum bot to your workspace - read-only on public channels, and it delivers your daily briefing. Connected once for your whole team.',
    connectUrlPath: '/api/surfaces/sources/slack-connect-url',
  },
  notion: {
    crumb: 'STEP 02 · NOTION',
    label: 'Notion',
    blurb: 'Read-only mirror of the pages your team shares with Terum. Connected once for your whole team by an admin.',
    connectUrlPath: '/api/surfaces/sources/notion-connect-url',
  },
};

export function TeamSourceConnectStep({ provider, onConnected, onBack }: {
  provider: TeamProvider;
  onConnected: () => void;
  onBack: () => void;
}) {
  const copy = TEAM_SOURCE[provider];
  const { open } = useOAuthPopup();
  const { data, refetch } = useOnboardingReadiness({ pollMs: 3000 });
  const { ensure } = useEnsureTeam();
  const [error, setError] = useState<string | null>(null);
  const [started, setStarted] = useState(false);
  const fired = useRef(false);

  useEffect(() => {
    if (data?.connectedProviders.includes(provider) && !fired.current) {
      fired.current = true;
      onConnected();
    }
  }, [data?.connectedProviders, provider, onConnected]);

  const connect = () => {
    setError(null);
    setStarted(true);
    open(
      async () => {
        // Ensure a team exists (create one if the user has none), then mint the connect URL
        // for it. Runs inside getUrl so the popup is already open on the click gesture.
        const teamId = await ensure();
        const { url } = await apiFetch<{ url: string }>(
          `${copy.connectUrlPath}?teamId=${encodeURIComponent(teamId)}`,
          { credentials: 'include' }
        );
        return url;
      },
      (payload) => {
        if (!payload.ok) {
          setError(payload.error || `Failed to connect ${copy.label}`);
          setStarted(false);
        } else {
          void refetch();
        }
      }
    );
  };

  return (
    <>
      <div className="crumb">{copy.crumb}</div>
      <div className="wh">Connect <span className="em">{copy.label}</span>.</div>
      <p className="wp">{copy.blurb}</p>
      <div style={{ marginTop: 22, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <button className="btn btn-primary" onClick={connect}>
          <span className="ltile" style={{ width: 18, height: 18 }}><SourceIcon id={provider} size={18} /></span>
          Authorize with {copy.label}
        </button>
        {error ? (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 12, color: 'var(--rose)' }}>⚠ {error}</span>
        ) : started ? (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 12, color: 'var(--inkS)' }}>
            <span className="dot acc pulse" /> Setting up your team &amp; authorizing…
          </span>
        ) : null}
      </div>
      <div className="wiz-foot">
        <button className="btn btn-secondary" onClick={onBack}>← Back to sources</button>
        <span className="prog">Step 2 · {copy.label}</span>
      </div>
    </>
  );
}
